/** Черновик «Подбора поставки» в localStorage браузера: отмеченные товары и количества по
 *  регионам. Формат v2 — `{v: 2, sel: [ключ товара], qty: {ключ региона: шт.}}` (ключи —
 *  supplyDraft.ts). Черновик v1 (`{v: 1, rows: {ключ строки оффер × регион: {checked, qty}}}`)
 *  переносится один раз, только пока v2 нет: отмеченная строка → отмечен её товар, количество — как
 *  было; после переноса v1 удаляется. «Очистить» пишет пустой v2 (а не удаляет ключ) — иначе старая
 *  вкладка с v1 воскресила бы черновик. Не прочитано / битые записи — текст для экрана, не молча;
 *  сырой текст битого черновика сохраняется под `<ключ>:broken`, чтобы первая правка не затёрла его. */
import { browserStorage, leafKey, MAX_QTY, productKey, type StorageLike } from "./supplyDraft";

/** Ключ localStorage с версией формата: другой формат — новый ключ. */
export const DRAFT_STORAGE_KEY = "seller-helper:wb-regions:supply-draft:v2";
/** Прежний формат (строки оффер × регион с галочками) — только для переноса. */
export const DRAFT_V1_KEY = "seller-helper:wb-regions:supply-draft:v1";
const DRAFT_VERSION = 2;

export interface Draft {
  /** отмеченные товары (ключ товара) */
  sel: ReadonlySet<string>;
  /** количество по региону товара (ключ региона); нет записи — не введено */
  qty: Readonly<Record<string, number>>;
}

export const EMPTY_DRAFT: Draft = { sel: new Set(), qty: {} };

export function isEmptyDraft(d: Draft): boolean {
  return d.sel.size === 0 && Object.keys(d.qty).length === 0;
}

export interface LoadResult {
  draft: Draft;
  /** не прочитано / битые записи / перенос v1 — текст для экрана (черновик тогда пустой или частичный) */
  error: string | null;
}

const validQty = (x: unknown): x is number =>
  typeof x === "number" && Number.isInteger(x) && x >= 0 && x <= MAX_QTY;

/** Ключ строки v1 `[кабинет, offer_key, регион]` → ключи товара и региона; чужое — null. */
function splitV1Key(k: string): { product: string; leaf: string } | null {
  let p: unknown;
  try {
    p = JSON.parse(k);
  } catch {
    return null;
  }
  if (!Array.isArray(p) || p.length !== 3) return null;
  const [legal, offer, region] = p as unknown[];
  if (typeof legal !== "string" || typeof offer !== "string") return null;
  if (region !== null && typeof region !== "string") return null;
  return { product: productKey(legal, offer), leaf: leafKey(legal, offer, region) };
}

function parseV2(raw: string): LoadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { draft: EMPTY_DRAFT, error: "черновик в браузере повреждён (не JSON) — начат пустой" };
  }
  const p = parsed as { v?: unknown; sel?: unknown; qty?: unknown } | null;
  if (
    typeof p !== "object" ||
    p === null ||
    p.v !== DRAFT_VERSION ||
    !Array.isArray(p.sel) ||
    typeof p.qty !== "object" ||
    p.qty === null ||
    Array.isArray(p.qty)
  ) {
    return { draft: EMPTY_DRAFT, error: "черновик в браузере в неизвестном формате — начат пустой" };
  }
  let bad = 0;
  const sel = new Set<string>();
  for (const k of p.sel as unknown[]) {
    if (typeof k === "string") sel.add(k);
    else bad += 1;
  }
  const qty: Record<string, number> = {};
  for (const [k, v] of Object.entries(p.qty as Record<string, unknown>)) {
    if (validQty(v)) qty[k] = v;
    else bad += 1;
  }
  return { draft: { sel, qty }, error: bad ? `в черновике браузера ${bad} битых записей — пропущены` : null };
}

/** v1 → v2. Битые записи — счётчик в тексте; не JSON / чужой формат — не переносится (текст). */
export function migrateV1(raw: string): LoadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { draft: EMPTY_DRAFT, error: "прежний черновик (v1) повреждён (не JSON) — не перенесён, удалён" };
  }
  const p = parsed as { v?: unknown; rows?: unknown } | null;
  if (typeof p !== "object" || p === null || p.v !== 1 || typeof p.rows !== "object" || p.rows === null) {
    return { draft: EMPTY_DRAFT, error: "прежний черновик (v1) в неизвестном формате — не перенесён, удалён" };
  }
  const sel = new Set<string>();
  const qty: Record<string, number> = {};
  let bad = 0;
  for (const [k, e] of Object.entries(p.rows as Record<string, unknown>)) {
    const keys = splitV1Key(k);
    const x = e as { checked?: unknown; qty?: unknown } | null;
    if (keys === null || typeof x !== "object" || x === null || typeof x.checked !== "boolean" || !(x.qty === null || validQty(x.qty))) {
      bad += 1;
      continue;
    }
    if (x.checked) sel.add(keys.product);
    if (x.qty !== null) qty[keys.leaf] = x.qty as number;
  }
  return {
    draft: { sel, qty },
    error: bad ? `при переносе прежнего черновика (v1) ${bad} битых записей пропущено` : null,
  };
}

/** Суффикс ключа, под которым лежит сырой текст не прочитавшегося черновика. */
export const BROKEN_SUFFIX = ":broken";

/** Сохранить сырой текст под `<ключ>:broken` (один раз: уже сохранённый не перетирается). */
function keepBroken(s: StorageLike, key: string, raw: string): string {
  const bk = key + BROKEN_SUFFIX;
  try {
    if (s.getItem(bk) === null) s.setItem(bk, raw);
    return `исходник сохранён в браузере под ключом ${bk}`;
  } catch (e) {
    return `исходник сохранить не удалось (${String(e)}) — первая правка затрёт его`;
  }
}

export function encodeDraft(d: Draft): string {
  return JSON.stringify({ v: DRAFT_VERSION, sel: [...d.sel], qty: d.qty });
}

/** Прочитать v2; его нет — перенести v1 (записать v2, удалить v1); битое — сырой текст в `:broken`. */
export function loadDraft(getStorage: () => StorageLike = browserStorage): LoadResult {
  let s: StorageLike;
  let raw: string | null;
  let rawV1: string | null;
  try {
    s = getStorage();
    raw = s.getItem(DRAFT_STORAGE_KEY);
    rawV1 = raw === null ? s.getItem(DRAFT_V1_KEY) : null;
  } catch (e) {
    return { draft: EMPTY_DRAFT, error: `черновик из браузера не прочитан (${String(e)}) — начат пустой` };
  }
  if (raw !== null) {
    const r = parseV2(raw);
    return r.error === null ? r : { draft: r.draft, error: `${r.error}; ${keepBroken(s, DRAFT_STORAGE_KEY, raw)}` };
  }
  if (rawV1 === null) return { draft: EMPTY_DRAFT, error: null };
  const m0 = migrateV1(rawV1);
  // v1 после переноса удаляется — битый v1 тоже сохраняем целиком
  const m = m0.error === null ? m0 : { draft: m0.draft, error: `${m0.error}; ${keepBroken(s, DRAFT_V1_KEY, rawV1)}` };
  try {
    if (!isEmptyDraft(m.draft)) s.setItem(DRAFT_STORAGE_KEY, encodeDraft(m.draft));
    s.removeItem(DRAFT_V1_KEY);
  } catch (e) {
    const note = `перенесённый черновик не сохранился в браузере (${String(e)}) — пропадёт при закрытии вкладки браузера или переключении вкладки/кабинета`;
    return { draft: m.draft, error: m.error ? `${m.error}; ${note}` : note };
  }
  return m;
}

/** Сохранить; ошибка — текст для экрана (черновик живёт, пока открыта вкладка «Подбор поставки»). */
export function saveDraft(draft: Draft, getStorage: () => StorageLike = browserStorage): string | null {
  try {
    const s = getStorage();
    // пустой — тоже запись (не removeItem): отсутствие v2 запускает перенос v1
    s.setItem(DRAFT_STORAGE_KEY, encodeDraft(draft));
    return null;
  } catch (e) {
    return `черновик не сохраняется в браузере (${String(e)}) — пропадёт при закрытии или переключении вкладки/кабинета`;
  }
}
